import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

type Classification = "stale_off" | "fresh_off" | "stable_on" | "fresh_on";

interface FlagRow {
  flag_key: string;
  enabled: boolean;
  description: string;
  updated_at: string;
  days_since_update: number;
  classification: Classification;
}

const CLASSIFICATION_LABEL: Record<
  Classification,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  stale_off: { label: "Antigo desligado", variant: "destructive" },
  fresh_off: { label: "Recente desligado", variant: "secondary" },
  stable_on: { label: "Estável ligado", variant: "default" },
  fresh_on: { label: "Recente ligado", variant: "outline" },
};

export default function FlagAudit() {
  const [rows, setRows] = useState<FlagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Classification | "all">("all");

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("flag_audit_view" as never)
        .select("*");
      if (!error && data) setRows(data as unknown as FlagRow[]);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const list = filter === "all" ? rows : rows.filter((r) => r.classification === filter);
    return [...list].sort((a, b) => b.days_since_update - a.days_since_update);
  }, [rows, filter]);

  return (
    <div className="container mx-auto py-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Auditoria de Flags</CardTitle>
          <p className="text-sm text-muted-foreground">
            Inventário operacional das feature flags em produção. Use para decidir quais
            promover, desligar ou dropar.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Filtrar:</span>
            <Select
              value={filter}
              onValueChange={(v) => setFilter(v as Classification | "all")}
            >
              <SelectTrigger className="w-[260px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="stale_off">Antigo desligado (≥14d)</SelectItem>
                <SelectItem value="fresh_off">Recente desligado (&lt;14d)</SelectItem>
                <SelectItem value="stable_on">Estável ligado (≥90d)</SelectItem>
                <SelectItem value="fresh_on">Recente ligado (&lt;90d)</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground ml-auto">
              {filtered.length} flag(s)
            </span>
          </div>

          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Flag</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Idade</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Atualizada em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const meta =
                    CLASSIFICATION_LABEL[r.classification] ?? {
                      label: r.classification,
                      variant: "outline" as const,
                    };
                  return (
                    <TableRow key={r.flag_key}>
                      <TableCell className="font-mono text-xs">{r.flag_key}</TableCell>
                      <TableCell>
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </TableCell>
                      <TableCell>{r.days_since_update}d</TableCell>
                      <TableCell className="max-w-md text-sm text-muted-foreground">
                        {r.description}
                      </TableCell>
                      <TableCell className="text-xs">
                        {new Date(r.updated_at).toLocaleString("pt-BR")}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      Nenhuma flag nessa classificação.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
